import { useEffect, useState } from "react";
import { Star, Quote, User, ChevronLeft, ChevronRight, ExternalLink } from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import PlatformLogo, { platformMeta } from "@/components/PlatformBadge";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

function resolvePhoto(url) {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  return `${BACKEND}${url}`;
}

function StarRow({ rating = 5 }) {
  const r = Math.max(0, Math.min(5, Number(rating) || 0));
  return (
    <div className="flex items-center gap-0.5" aria-label={`Rating: ${r} out of 5`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          size={14}
          className={i < r ? "fill-[#f5b301] text-[#f5b301]" : "text-line"}
          strokeWidth={1.5}
        />
      ))}
    </div>
  );
}

function initials(name = "") {
  return (name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() || "")
    .join("");
}

// LinkedIn reviews get their own layout: avatar/name/role up top (like a
// LinkedIn post header), then the post's description, then its image, then
// a plain "View Post" link -- matching the reference design the user wants
// for LinkedIn specifically. Other platforms keep the original card below.
function LinkedInReviewCard({ review }) {
  const photo = resolvePhoto(review.photo_url);
  return (
    <div
      data-testid={`review-card-${review.id}`}
      // "group" lets only the View Post footer react on hover, instead of
      // the whole card -- see the footer below.
      className="group h-full min-h-[362px] rounded-2xl border border-line bg-white p-3 flex flex-col overflow-hidden"
    >
      <div className="flex items-start gap-3">
        {photo ? (
          <img
            src={photo}
            alt={review.name}
            className="h-12 w-12 rounded-full object-cover shrink-0"
            loading="lazy"
          />
        ) : (
          <div className="h-12 w-12 rounded-full bg-cyan-soft text-royal-deep flex items-center justify-center font-mono-ui text-[14px] font-bold shrink-0">
            {initials(review.name) || <User size={18} />}
          </div>
        )}
        <div className="flex-1 min-w-0">
          {/* Plain body font (not the site's display face) on name, role, and
              description -- matches the neutral sans LinkedIn itself uses,
              so the card reads like an authentic LinkedIn post. */}
          <p className="font-sans text-[14.5px] font-bold text-ink truncate">
            {review.name}
          </p>
          <p className="font-sans text-[11.5px] text-ink-soft truncate">
            {[review.role, review.company].filter(Boolean).join(" · ") || " "}
          </p>
        </div>
      </div>
      <p className="mt-4 font-sans text-[14.5px] text-ink leading-relaxed line-clamp-2">
        {review.review_text}
      </p>
      {/* Fixed (not flex-grown) height so cards line up evenly whether or not
          there's an image, and so a tall/portrait uploaded image can never
          blow out the card -- a percentage height on the <img> can't do
          that safely since it depends on the parent already having a
          definite height, which a flex-grown box doesn't reliably have. */}
      <div className="mt-4 h-48 overflow-hidden rounded-lg">
        {review.post_image_url && (
          <img
            src={resolvePhoto(review.post_image_url)}
            alt=""
            className="h-full w-full border border-line object-cover"
            loading="lazy"
          />
        )}
      </div>
      {/* mt-auto sticks this to the bottom of the card regardless of how
          short the content above is; negative margins extend it
          edge-to-edge so the whole footer, not just the text, highlights
          on card hover -- same light-blue chip colour as the LinkedIn
          filter tab's active state. */}
      {review.review_url && (
        <div className="-mx-3 -mb-3 mt-auto border-t border-line px-3 pt-4 pb-3.5 transition-colors group-hover:bg-[var(--cyan-tint)]">
          <a
            href={review.review_url}
            target="_blank"
            rel="noopener noreferrer nofollow"
            data-testid={`review-link-${review.id}`}
            className="block text-center text-[13.5px] font-bold text-royal-deep"
          >
            View Post
          </a>
        </div>
      )}
    </div>
  );
}

export function ReviewCard({ review }) {
  if (review.platform === "linkedin") {
    return <LinkedInReviewCard review={review} />;
  }
  const photo = resolvePhoto(review.photo_url);
  const meta = platformMeta(review.platform);
  // Mirrors LinkedInReviewCard's exact box model (padding, header layout,
  // description clamp, fixed image height, sticky footer) so Trustpilot/
  // other-platform cards are the same width and height as LinkedIn cards.
  return (
    <div
      data-testid={`review-card-${review.id}`}
      className="group h-full min-h-[362px] rounded-2xl border border-line bg-white p-3 flex flex-col overflow-hidden"
    >
      {meta && (
        <div className="flex justify-end">
          <span className="shrink-0 inline-flex items-center gap-1 rounded-full border border-line bg-bg-alt px-2 py-1 text-[10.5px] font-semibold text-ink-soft">
            <PlatformLogo platform={review.platform} size={12} />
            {meta.label}
          </span>
        </div>
      )}
      {/* Profile row starts below the platform badge (instead of sharing
          its row) so the name/role text has full width and doesn't get
          squeezed into truncating. */}
      <div className={`flex items-start gap-3 ${meta ? "mt-3" : ""}`}>
        {photo ? (
          <img
            src={photo}
            alt={review.name}
            className="h-12 w-12 rounded-full object-cover shrink-0"
            loading="lazy"
          />
        ) : (
          <div className="h-12 w-12 rounded-full bg-cyan-soft text-royal-deep flex items-center justify-center font-mono-ui text-[14px] font-bold shrink-0">
            {initials(review.name) || <User size={18} />}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="font-sans text-[14.5px] font-bold text-ink truncate">
            {review.name}
          </p>
          <p className="font-sans text-[11.5px] text-ink-soft truncate">
            {[review.role, review.company].filter(Boolean).join(" · ") || "\u00A0"}
          </p>
          {review.rating ? (
            <div className="mt-1.5">
              <StarRow rating={review.rating} />
            </div>
          ) : null}
        </div>
      </div>
      <Quote size={20} className="mt-3 text-royal/20" aria-hidden />
      {/* With no post image there's no fixed box to fill, so let the
          description run to more lines instead of leaving the space below
          it blank. */}
      <p
        className={`mt-1 font-sans text-[14.5px] text-ink leading-relaxed ${
          review.post_image_url ? "line-clamp-2" : "line-clamp-[7]"
        }`}
      >
        {review.review_text}
      </p>
      {review.post_image_url && (
        <div className="mt-4 h-48 overflow-hidden rounded-lg">
          <img
            src={resolvePhoto(review.post_image_url)}
            alt=""
            className="h-full w-full border border-line object-cover"
            loading="lazy"
          />
        </div>
      )}
      {review.review_url && (
        <div className="-mx-3 -mb-3 mt-auto border-t border-line px-3 pt-4 pb-3.5 transition-colors group-hover:bg-[var(--cyan-tint)]">
          <a
            href={review.review_url}
            target="_blank"
            rel="noopener noreferrer nofollow"
            data-testid={`review-link-${review.id}`}
            className="inline-flex w-full items-center justify-center gap-1.5 text-[13.5px] font-bold text-royal-deep"
          >
            View review{meta ? ` on ${meta.label}` : ""} <ExternalLink size={13} />
          </a>
        </div>
      )}
    </div>
  );
}

export function SubSection({ reviews, testid, perRow = 3 }) {
  if (!reviews || reviews.length === 0) return null;
  const lgBasisClass = perRow === 4 ? "lg:basis-1/4" : "lg:basis-1/3";
  return (
    <div data-testid={testid} className="mt-8 first:mt-0">
      <Carousel
        opts={{ align: "start", loop: reviews.length > perRow }}
        className="w-full"
      >
          <CarouselContent className="-ml-3">
            {reviews.map((r) => (
              <CarouselItem
                key={r.id}
                className={`pl-3 basis-full sm:basis-1/2 ${lgBasisClass}`}
              >
                <div className="h-full">
                  <ReviewCard review={r} />
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
          {reviews.length > perRow && (
            <>
              <CarouselPrevious
                data-testid={`${testid}-prev`}
                variant="ghost"
                // Offset stays inside the section's own side padding (px-5 at
                // md, lg:px-10 at lg+) so the button never gets clipped by
                // the section's overflow-hidden or causes horizontal overflow.
                className="hidden md:inline-flex -left-4 lg:-left-9 h-10 w-10 border-0 bg-white text-ink-soft shadow-md hover:bg-cyan-soft hover:text-royal-deep"
              >
                <ChevronLeft size={18} />
              </CarouselPrevious>
              <CarouselNext
                data-testid={`${testid}-next`}
                variant="ghost"
                className="hidden md:inline-flex -right-4 lg:-right-9 h-10 w-10 border-0 bg-white text-ink-soft shadow-md hover:bg-cyan-soft hover:text-royal-deep"
              >
                <ChevronRight size={18} />
              </CarouselNext>
            </>
          )}
      </Carousel>
    </div>
  );
}

export default function ReviewsSection({
  courseSlug,
  category,
  eyebrow = "07 · Voices from the community",
  heading = "What our learners are saying.",
  intro = "Real reviews from individual professionals and corporate teams who trained with ZaranTech across SAP, Workday, AI, and Microsoft programs.",
  testidRoot = "reviews-section",
  className = "relative py-14 lg:py-20 bg-bg-alt overflow-hidden",
  // "left" renders the header in the course-page house style (section-badge
  // pill + smaller heading) so it matches the other course sections; the
  // homepage keeps the default numbered mono eyebrow + large heading.
  align = "center",
  // Kept at the course/about-page width by default; the homepage overrides
  // this to max-w-[1440px] to match its other sections.
  maxWidthClass = "max-w-[1240px]",
  // Cards visible per row at desktop width; the homepage overrides this to
  // 4, other pages keep the original 3.
  cardsPerRow = 3,
}) {
  const isLeft = align === "left";
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  // True when the section is showing site-wide reviews because this course
  // has no course-specific reviews of its own yet. We tweak copy accordingly.
  const [isFallback, setIsFallback] = useState(false);
  // Platform filter tabs: "linkedin" / "trustpilot" show only that platform;
  // "others" shows everything that isn't LinkedIn or Trustpilot.
  const [platformFilter, setPlatformFilter] = useState("linkedin");

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const fetchList = async (url) => {
        try {
          const r = await fetch(url);
          if (!r.ok) return [];
          const data = await r.json();
          return Array.isArray(data.items) ? data.items : [];
        } catch {
          return [];
        }
      };

      // 1. On a course page, request reviews pinned to this course OR tagged
      //    with its category (e.g. an SAP review shows on all SAP courses).
      const targeted = courseSlug || category;
      let list = [];
      if (targeted) {
        const params = new URLSearchParams();
        if (courseSlug) params.set("course_slug", courseSlug);
        if (category) params.set("category", category);
        list = await fetchList(`${BACKEND}/api/public/reviews?${params.toString()}`);
      }
      // 2. If a course page has no targeted reviews, fall back to the site-wide
      //    homepage set so the section still appears.
      let fallback = false;
      if (targeted && list.length === 0) {
        list = await fetchList(`${BACKEND}/api/public/reviews`);
        fallback = list.length > 0;
      } else if (!targeted) {
        list = await fetchList(`${BACKEND}/api/public/reviews`);
      }

      if (cancelled) return;
      setItems(list);
      setIsFallback(fallback);
      setLoaded(true);
    };
    load();
    return () => { cancelled = true; };
  }, [courseSlug, category]);

  const PLATFORM_TABS = [
    { key: "linkedin", label: "LinkedIn" },
    { key: "trustpilot", label: "Trustpilot" },
    { key: "others", label: "Others" },
  ];
  const matchesFilter = (r) => {
    if (platformFilter === "linkedin") return r.platform === "linkedin";
    if (platformFilter === "trustpilot") return r.platform === "trustpilot";
    return r.platform !== "linkedin" && r.platform !== "trustpilot";
  };
  const filteredItems = items.filter(matchesFilter);
  const corporate = filteredItems.filter((r) => r.learner_type === "corporate");
  const individual = filteredItems.filter((r) => r.learner_type === "individual");

  // Hide only when there are genuinely zero reviews anywhere on the site.
  if (loaded && items.length === 0) return null;

  // When we're falling back to site-wide reviews on a course page, use a
  // generic heading instead of pretending they were about this specific course.
  const resolvedHeading = isFallback
    ? "What our learners are saying."
    : courseSlug
      ? "What our past learners are saying."
      : heading;
  const resolvedIntro = isFallback
    ? "Real reviews from individual professionals and corporate teams who trained with ZaranTech across SAP, Workday, AI, and Microsoft programs."
    : courseSlug
      ? "Real reviews from professionals and teams who completed the training with ZaranTech."
      : intro;
  const resolvedEyebrow = isFallback ? "Learner voices" : eyebrow;

  return (
    <section
      id={courseSlug ? `reviews-${courseSlug}` : "reviews"}
      data-testid={testidRoot}
      className={className}
    >
      <div className="orb orb-cyan h-[320px] w-[320px] -top-20 -right-16 opacity-30" aria-hidden />
      <div className="absolute inset-0 -z-10 bg-grid opacity-25" aria-hidden />
      <div className={`relative mx-auto ${maxWidthClass} px-5 lg:px-10`}>
        <div className="max-w-3xl">
          {isLeft ? (
            <span className="section-badge">{resolvedEyebrow}</span>
          ) : (
            <p className="font-mono-ui text-[11px] uppercase tracking-[0.22em] text-royal">
              {resolvedEyebrow}
            </p>
          )}
          <h2
            className={`mt-3 font-display font-bold text-ink leading-tight text-balance ${
              isLeft
                ? "text-2xl md:text-3xl"
                : "text-3xl md:text-4xl lg:text-[44px] leading-[1.1]"
            }`}
          >
            {resolvedHeading}
          </h2>
          <p className="mt-4 text-[15.5px] text-ink-soft leading-relaxed max-w-2xl">
            {resolvedIntro}
          </p>
        </div>

        {/* A bordered, padded container around the buttons reads as one
            grouped control (like a segmented switch) rather than loose
            floating tags. */}
        <div
          data-testid={`${testidRoot}-platform-tabs`}
          className="mt-7 inline-flex flex-wrap items-center gap-1 rounded-xl border border-line bg-bg-alt p-1.5"
        >
          {PLATFORM_TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setPlatformFilter(tab.key)}
              data-testid={`${testidRoot}-platform-tab-${tab.key}`}
              className={`inline-flex items-center gap-1.5 rounded-lg px-4 py-1.5 text-[12.5px] font-semibold transition-colors ${
                platformFilter === tab.key
                  ? "bg-white text-royal-deep shadow-md ring-2 ring-[var(--royal)]"
                  : "text-ink-soft hover:text-royal-deep"
              }`}
            >
              {(tab.key === "linkedin" || tab.key === "trustpilot") && (
                <PlatformLogo platform={tab.key} size={16} />
              )}
              {tab.label}
            </button>
          ))}
        </div>

        {filteredItems.length === 0 ? (
          <p className="mt-8 text-[14px] text-ink-soft">
            No {PLATFORM_TABS.find((t) => t.key === platformFilter)?.label.toLowerCase()} reviews yet.
          </p>
        ) : (
          <>
            <SubSection
              reviews={corporate}
              testid={`${testidRoot}-corporate`}
              perRow={cardsPerRow}
            />

            <SubSection
              reviews={individual}
              testid={`${testidRoot}-individual`}
              perRow={cardsPerRow}
            />
          </>
        )}
      </div>
    </section>
  );
}
